/**
 * PolylineGeometryNode - Converts polylines to Three.js line or tube geometry
 *
 * Takes polylines ({x,y,z}[][]) and outputs Three.js LineSegments or tube Mesh.
 * Supports per-polyline vertex colors and variable-radius tube mode.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import type { Payload } from '@graph/Payload';
import { polylinesToGeometry, polylinesToTubes } from '../polylineUtils';

const polylineGeometryEndpoints = {
  ...internals({
    polylines: { dataType: 'polylines', primary: true, label: 'Polylines' },
    meshOut: { dataType: 'three:group', isOutput: true }
  }),
  ...params({
    mode: { dataType: 'string', value: 'line', options: ['line', 'tube'], label: 'Mode' },
    radius: { value: 0.05, min: 0.001, max: 2, step: 0.01 },
    radiusProfile: { dataType: 'array', value: [1], label: 'Radius Profile' },
    radialSegments: { value: 6, min: 3, max: 32, step: 1, label: 'Radial Segments' },
  })
};

class PolylineGeometryNode extends BaseNode<typeof polylineGeometryEndpoints> {
  private three: Three;
  private storedPolylines: any = null;
  private storedMesh: any = null;
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  buildMesh(): any {
    if (!this.storedPolylines || !Array.isArray(this.storedPolylines) || this.storedPolylines.length === 0) {
      return null;
    }

    if (this.params.mode.value === 'tube') {
      return this.buildTubeMesh();
    }
    return this.buildLineMesh();
  }

  private buildLineMesh(): any {
    const geometry = polylinesToGeometry(this.three, this.storedPolylines);
    const material = new this.three.LineBasicMaterial({ vertexColors: true });
    return new this.three.LineSegments(geometry, material);
  }

  private buildTubeMesh(): any {
    const { geometry, material } = polylinesToTubes(this.three, this.storedPolylines, {
      radius: this.params.radius.value as number,
      radiusProfile: this.params.radiusProfile.value as number[],
      radialSegments: this.params.radialSegments.value as number,
    });
    return new this.three.Mesh(geometry, material);
  }

  setThreeObject(mesh: any): void {
    this.storedMesh = mesh;
  }

  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  onInternalChanged(payload: Payload, _endpointId: string): void {
    this.storedPolylines = payload?.value;
    this.rebuildCallback?.();
  }

  onParamChanged(): void {
    this.rebuildCallback?.();
  }
}

export const polylineGeometryNodeDef: NodeDef<PolylineGeometryNode> = {
  type: 'PolylineGeometry',
  shortName: 'polyline-geo',
  category: 'geometry',
  info: 'Converts polylines to Three.js line segments or tube geometry.',
  endpoints: polylineGeometryEndpoints,
  ctor: PolylineGeometryNode
};
